import { useEffect, useMemo, useState } from 'react'

import InputField from 'components/Form/InputField/InputField'
import { addToast } from 'components/Toast/Toast'
import { sendContactMessage } from 'services/endpoints/tour/contact'

import './_ContactUs.scss'

// If you prefer to keep translations, swap plain strings for t('...')
// import { useTranslation } from 'react-i18next'

interface TextAreaFieldProps {
  id: string
  value: string
  changeFn: (v: string) => void
  additionalProps?: React.TextareaHTMLAttributes<HTMLTextAreaElement>
  loginAttempted?: boolean
}

const TextAreaField = ({
  id,
  value,
  changeFn,
  additionalProps,
  loginAttempted
}: TextAreaFieldProps) => {
  return (
    <div
      className={`input-field contact-message-box ${
        loginAttempted && !value.trim() ? 'input-field--error' : ''
      }`}
    >
      <textarea
        id={id}
        className='contact-message-box__input'
        value={value}
        onChange={(e) => changeFn(e.target.value)}
        {...additionalProps}
      />
    </div>
  )
}

interface ContactFormProps {
  fromUser?: boolean
  setActivePanel?: (panel: string) => void
  onClose?: () => void
}

/** EMAIL + BASIC VALIDATION */
const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())

export const ContactUs = ({
  fromUser: _fromUser = false,
  setActivePanel,
  onClose
}: ContactFormProps) => {
  // const { t } = useTranslation()

  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [username, setUsername] = useState('')
  const [message, setMessage] = useState('')

  const [sending, setSending] = useState(false)
  const [attempted, setAttempted] = useState(false)

  const [isMobileView, setIsMobileView] = useState(false)
  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth <= 768)
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const errors = useMemo(
    () => ({
      email: !email || !isEmail(email),
      name: !name.trim(),
      message: message.trim().length < 5
    }),
    [email, name, message]
  )

  const hasErrors = errors.email || errors.name || errors.message

  const submitForm = async () => {
    setAttempted(true)
    if (hasErrors) {
      addToast('error', 'Please fix the highlighted fields.')
      return
    }
    try {
      setSending(true)
      await sendContactMessage({
        email: email.trim(),
        name: name.trim(),
        username: username.trim() || null,
        message: message.trim()
      })
      addToast('success', 'Thanks! Your message has been sent.')
      setEmail('')
      setName('')
      setUsername('')
      setMessage('')
      setAttempted(false)
    } catch (err: any) {
      addToast('error', err?.message || 'Something went wrong sending your message.')
    } finally {
      setSending(false)
    }
  }

  return (
    <form
      className='auth-login__form'
      autoComplete='on'
      onSubmit={(e) => {
        e.preventDefault()
        void submitForm()
      }}
    >
      <h1 className='auth-register__title'>Contact Us</h1>

      {!isMobileView && (
        <div className='divider'>
          <span>We’ll get back to you</span>
        </div>
      )}

      <InputField
        id='contact-email'
        type='email'
        value={email}
        changeFn={setEmail}
        additionalProps={{
          name: 'email',
          autoComplete: 'email',
          placeholder: 'Email *'
        }}
        loginAttempted={attempted && errors.email}
      />

      <InputField
        id='contact-name'
        type='text'
        value={name}
        changeFn={setName}
        additionalProps={{
          name: 'name',
          autoComplete: 'name',
          placeholder: 'Full name *'
        }}
        loginAttempted={attempted && errors.name}
      />

      <InputField
        id='contact-username'
        type='text'
        value={username}
        changeFn={setUsername}
        additionalProps={{
          name: 'username',
          autoComplete: 'username',
          placeholder: 'Username (optional)'
        }}
        loginAttempted={false}
      />

      <TextAreaField
        id='contact-message'
        value={message}
        changeFn={setMessage}
        additionalProps={{
          name: 'message',
          placeholder: 'Your message *',
          rows: 6
        }}
        loginAttempted={attempted && errors.message}
      />
      <div className='contact-us-submit-btn'>
        <button
          type='button'
          className='auth-login__back-btn contact-us-back-btn'
          onClick={() => {
            if (onClose) {
              onClose()
            } else {
              setActivePanel?.('default')
            }
          }}
        >
          Back
        </button>

        <button
          type='submit'
          className='button button--blue submit-btn contact-us-send-btn'
          disabled={sending}
        >
          {sending ? 'Sending…' : 'Send'}
        </button>
      </div>
    </form>
  )
}
